import { useCallback } from 'react';

import type { z } from 'zod';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';
import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import { useGenerateAiDraft } from './hooks/useGenerateAiDraft';
import { schema } from './schema';

type FormData = z.infer<typeof schema>;

type UsePersonalPromptReturn = ModelHookResult<
	{
		schema: typeof schema;
		defaultValues: FormData;
		loading: boolean;
		error: Error | null;
	},
	{
		back: () => void;
		submit: (formData: FormData) => void;
		persistDraft: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
	}
>;

export function usePersonalPrompt(): UsePersonalPromptReturn {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();

	const { mutate, error, mutationState } = useGenerateAiDraft();

	const back = useCallback(() => {
		void track('sap_personal_story_back');
		actions.send({ type: 'back' });
	}, [actions, track]);

	const persistDraft = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			const personalStory = event.target.value.trim();
			const userInput = { personalStory };
			actions.send({ type: 'submit', draft: { input: userInput }, target: 'self' });
		},
		[actions],
	);

	return {
		data: {
			loading: isLoading(mutationState) || isLoaded(mutationState),
			schema,
			defaultValues: { personalStory: data.draft?.input?.personalStory ?? '' },
			error,
		},
		actions: {
			back,
			submit: mutate,
			persistDraft,
		},
	};
}
